import { Check, Dot, MoreHorizontal } from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";

import AvatarText from "@/components/reusables/AvatarText";
import * as Dropdown from "@/components/ui/dropdown-menu";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { Notification } from "@/interfaces/interfaces";
import { cn, renderFullName } from "@/lib/utils";
import { useSingleReadNotificationMutation } from "@/redux/api/generalQuery";
import { getRelativeTimeFromNow } from "@/utils/helper-functions/date-time.helper";

type NotificationBodyProps = {
  open: boolean;
  children: React.JSX.Element;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCount: React.Dispatch<React.SetStateAction<number>>;
  notifications: Notification[];
  isLoading: boolean;
  intersectionCallbackRef: (node: HTMLDivElement | null) => void;
  handleReadAll: () => Promise<void>;
};

const NotificationOptions = ({
  handleReadAll,
}: {
  handleReadAll: () => Promise<void>;
}): React.JSX.Element => {
  return (
    <Dropdown.DropdownMenu>
      <Dropdown.DropdownMenuTrigger>
        {" "}
        <MoreHorizontal className='w-4 h-4' />
      </Dropdown.DropdownMenuTrigger>
      <Dropdown.DropdownMenuContent className='min-w-40 min-h-10'>
        <Dropdown.DropdownMenuItem onClick={() => handleReadAll()}>
          <span>Mark All As Read</span>
          <Check className='w-5 h-5 ml-2 ' />
        </Dropdown.DropdownMenuItem>
      </Dropdown.DropdownMenuContent>
    </Dropdown.DropdownMenu>
  );
};

const NotificationBody = (props: NotificationBodyProps): React.JSX.Element => {
  const {
    open,
    children,
    setOpen,
    setCount,
    notifications,
    isLoading,
    intersectionCallbackRef,
    handleReadAll,
  } = props;

  const location = useLocation();

  const isEmployeePage =
    location.pathname === `${ROUTES.WORKFORCE}${ROUTES.EMPLOYEE}`;

  const Comp = isEmployeePage ? "div" : Link;

  const [singleReadNotification, { isLoading: isLoading2 }] =
    useSingleReadNotificationMutation();

  const handleSingleRead = async (
    id: string,
    isRead: boolean,
  ): Promise<void> => {
    if (!id || isLoading2) {
      return;
    }

    await singleReadNotification({
      notificationId: id,
      data: {
        isRead: !isRead,
      },
    });
  };

  const handleExit = (): void => {
    setOpen(!open);
    setCount(0);
  };

  return (
    <div className='relative flex items-center'>
      <Dropdown.DropdownMenu open={open} onOpenChange={handleExit}>
        <Dropdown.DropdownMenuTrigger asChild>
          {children}
        </Dropdown.DropdownMenuTrigger>
        <Dropdown.DropdownMenuContent
          side='right'
          className='relative px-2 pt-0 mb-10 overflow-y-auto shadow-lg h-96 w-96 max-w-96 scrollbar-thin'
        >
          <div className='sticky top-0 z-40 flex items-center justify-between w-full bg-white border-0 border-b-2 h-14'>
            <h2 className='p-4 text-lg font-medium '>Notifications</h2>
            <NotificationOptions handleReadAll={handleReadAll} />
          </div>

          {notifications?.length > 0 && !isLoading ? (
            notifications?.map((item, idx) => {
              const name = renderFullName({
                params: item?.notification?.employee?.user,
              });

              return (
                <div
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                  }}
                >
                  <Dropdown.DropdownMenuItem className='p-2 border-b'>
                    <Comp
                      to={
                        isEmployeePage
                          ? "#"
                          : `${ROUTES.WORKFORCE}${ROUTES.EMPLOYEE}`
                      }
                      state={{
                        activeTab: "AWAITING",
                      }}
                      className='grid grid-cols-[1fr_0.1fr] items-center'
                    >
                      <AvatarText
                        name={name || "-"}
                        title={name}
                        subtitle={item?.notification?.content}
                        custom={{
                          node: (
                            <span className='text-xs font-thin text-black/40'>
                              {getRelativeTimeFromNow(item?.createdAt)}
                            </span>
                          ),
                          position: "subtitle",
                        }}
                        classNames={{
                          avatarClassName: " flex self-start",
                          titleClassName: "font-medium ",
                        }}
                      />
                      <div
                        title={item?.isRead ? "Mark as Unread" : "Mark as Read"}
                      >
                        <Dot
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleSingleRead(
                              item?.notificationId,
                              item.isRead,
                            );
                          }}
                          className={cn(
                            "size-12 hover:border rounded-full",
                            item?.isRead
                              ? "text-secondary "
                              : "text-primary  rounded-full",
                          )}
                        />
                      </div>

                      {/* <div className='w-2 h-2 border rounded-full bg-primary border-primary' /> */}
                    </Comp>
                  </Dropdown.DropdownMenuItem>
                </div>
              );
            })
          ) : (
            <div className='flex items-center justify-center w-full h-full'>
              <p className='text-sm text-gray-500'>
                {isLoading ? "Loading notifications..." : "No notifications"}
              </p>
            </div>
          )}
          <div className='mb-8' ref={intersectionCallbackRef} />
        </Dropdown.DropdownMenuContent>
      </Dropdown.DropdownMenu>
    </div>
  );
};

export default NotificationBody;
